import React, { useEffect, useRef, useState } from "react";
import { X, Terminal as TerminalIcon, Wifi, WifiOff } from "lucide-react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";

// In produzione REACT_APP_BACKEND_URL='' → usa l'origin corrente per il WebSocket
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function WebTerminalModal({ rigId, rigName, open, onClose, initialCommand }) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const sentInitialRef = useRef(false);
  const [status, setStatus] = useState("connecting"); // connecting | connected | disconnected | error

  useEffect(() => {
    if (!open) return;
    if (!containerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: "IBM Plex Mono, Menlo, monospace",
      fontSize: 13,
      lineHeight: 1.25,
      theme: {
        background: "#0B0B0F",
        foreground: "#FAFAFA",
        cursor: "#10B981",
        cursorAccent: "#0B0B0F",
        black: "#000000",
        red: "#EF4444",
        green: "#10B981",
        yellow: "#F59E0B",
        blue: "#3B82F6",
        magenta: "#A78BFA",
        cyan: "#22D3EE",
        white: "#E4E4E7",
        brightBlack: "#52525B",
        brightRed: "#F87171",
        brightGreen: "#34D399",
        brightYellow: "#FBBF24",
        brightBlue: "#60A5FA",
        brightMagenta: "#C4B5FD",
        brightCyan: "#67E8F9",
        brightWhite: "#FAFAFA",
        selectionBackground: "rgba(16, 185, 129, 0.3)",
      },
      allowProposedApi: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);

    termRef.current = term;
    fitRef.current = fit;

    setTimeout(() => {
      try { fit.fit(); } catch (e) { /* noop */ }
    }, 100);

    const token = localStorage.getItem("warthive_token") || "";
    const wsUrl =
      BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws") +
      `/api/terminal/${rigId}?token=${encodeURIComponent(token)}`;

    term.write("\x1b[2m· connessione a " + rigName + "…\x1b[0m\r\n");

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("connected");
      // send initial resize
      try {
        const { cols, rows } = term;
        ws.send(JSON.stringify({ type: "shell_resize", cols, rows }));
      } catch (e) { /* noop */ }
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "shell_output" && typeof msg.data === "string") {
          term.write(msg.data);
          // Auto-run initial command once the prompt (PS1) appears
          if (!sentInitialRef.current && initialCommand && msg.data.match(/[#$]\s*$/)) {
            sentInitialRef.current = true;
            // small delay so the prompt is fully painted
            setTimeout(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: "shell_input", data: initialCommand + "\n" }));
              }
            }, 200);
          }
        }
      } catch (e) { /* noop */ }
    };
    ws.onclose = () => {
      setStatus("disconnected");
      term.write("\r\n\x1b[2m· connessione chiusa\x1b[0m\r\n");
    };
    ws.onerror = () => {
      setStatus("error");
    };

    const onData = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "shell_input", data }));
      }
    });
    const onResize = term.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "shell_resize", cols, rows }));
      }
    });
    const handleWindowResize = () => {
      try { fit.fit(); } catch (e) { /* noop */ }
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      onData.dispose();
      onResize.dispose();
      try { ws.close(); } catch (e) { /* noop */ }
      try { term.dispose(); } catch (e) { /* noop */ }
      termRef.current = null;
      wsRef.current = null;
      fitRef.current = null;
      sentInitialRef.current = false;
    };
  }, [open, rigId, rigName, initialCommand]);

  if (!open) return null;

  const StatusIcon = status === "connected" ? Wifi : WifiOff;
  const statusColor = {
    connecting: "text-[#F59E0B]",
    connected: "text-[#10B981]",
    disconnected: "text-[#A1A1AA]",
    error: "text-[#EF4444]",
  }[status];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div
        className="bento p-0 w-full max-w-5xl h-[80vh] flex flex-col"
        data-testid="terminal-modal"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A]">
          <div className="flex items-center gap-3">
            <TerminalIcon className="w-4 h-4 text-[#10B981]" />
            <div className="font-heading font-bold text-sm">
              Terminal · <span className="text-[#10B981]">{rigName}</span>
            </div>
            <div className={`flex items-center gap-1 ml-2 ${statusColor}`}>
              <StatusIcon className="w-3 h-3" />
              <span className="font-mono text-[10px] uppercase tracking-wider">{status}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="terminal-close-btn"
            className="text-[#A1A1AA] hover:text-[#FAFAFA] p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          ref={containerRef}
          data-testid="terminal-body"
          className="flex-1 bg-[#0B0B0F] p-2 overflow-hidden"
        />
        <div className="px-4 py-2 border-t border-[#27272A] font-mono text-[10px] text-[#A1A1AA] flex items-center justify-between">
          <span>Shell come root sul rig — Ctrl+C / Ctrl+D / clear funzionano normalmente.</span>
          <span>WS · xterm.js</span>
        </div>
      </div>
    </div>
  );
}
